// ManiDine Restaurant Web — src/components/IncomingOrderModal.jsx — Updated: 2026-04-06 18:20 (America/Edmonton)
// FILE: src/components/IncomingOrderModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { auth, db } from "../firebase";

const CANCEL_ORDER_URL =
  "https://cancelorder-cnukicngsa-uc.a.run.app";

const formatDateTime = (timestamp) => {
  if (!timestamp) return "";
  const date =
    typeof timestamp.toDate === "function" ? timestamp.toDate() : new Date(timestamp);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
  }).format(value || 0);

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const getLineSubtotal = (list) =>
  (list || []).reduce(
    (sum, item) => sum + (toNumber(item?.price) || 0) * (toNumber(item?.quantity) || 0),
    0
  );

const round2 = (value) =>
  Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;

const normalizeTaxRate = (value) => {
  const parsed = toNumber(value);
  if (parsed == null) return null;
  if (parsed > 1 && parsed <= 100) return parsed / 100;
  return parsed >= 0 ? parsed : null;
};

const getOriginalSubtotal = (order) =>
  toNumber(order?.subtotal) ??
  toNumber(order?.subTotal) ??
  toNumber(order?.itemSubtotal) ??
  getLineSubtotal(order?.items || []);

const getOriginalTaxAmount = (order) =>
  toNumber(order?.taxAmount) ??
  toNumber(order?.tax) ??
  0;

const getOriginalTotal = (order, originalSubtotal) =>
  toNumber(order?.total) ??
  toNumber(order?.totalAmount) ??
  toNumber(order?.grandTotal) ??
  originalSubtotal;

const getEffectiveTaxRate = (order, originalSubtotal, originalTaxAmount) => {
  const explicitRate = normalizeTaxRate(order?.taxRate);
  if (explicitRate != null) return explicitRate;
  if (originalSubtotal > 0 && originalTaxAmount > 0) {
    return round2(originalTaxAmount / originalSubtotal);
  }
  return 0;
};

const calculateTotals = (list, order) => {
  const subtotalCalc = getLineSubtotal(list);

  const originalSubtotal = getOriginalSubtotal(order);
  const originalTaxAmount = round2(getOriginalTaxAmount(order));
  const originalTotal = getOriginalTotal(order, originalSubtotal);
  const taxRate = getEffectiveTaxRate(order, originalSubtotal, originalTaxAmount);

  const preservedNonTaxCharges = round2(
    Math.max(0, originalTotal - originalSubtotal - originalTaxAmount)
  );

  const taxAmount = round2(subtotalCalc * taxRate);
  const totalCalc = round2(subtotalCalc + taxAmount + preservedNonTaxCharges);

  return {
    subtotal: subtotalCalc,
    taxRate,
    taxAmount,
    total: totalCalc,
    preservedNonTaxCharges,
  };
};

const isPickupOrder = (order) => {
  const ft = String(order?.fulfillmentType || order?.deliveryMode || "").toLowerCase();
  const deliveryOption = String(order?.deliveryOption || "").toLowerCase();
  return ft === "pickup" || deliveryOption === "pickup";
};

const isScheduledOrder = (order) => {
  const timeMode = String(order?.timeMode || "").toLowerCase();
  return !!order?.isScheduled || timeMode === "later";
};

const getScheduleLabel = (order) => {
  if (!isScheduledOrder(order)) return "";

  return String(
    order?.scheduledTimeLabel ||
      order?.timeLabel ||
      order?.scheduledWindowLabel ||
      order?.scheduledForLabel ||
      order?.etaLabel ||
      ""
  ).trim();
};

// ✅ Single combined tag text shown in header
const getHeaderTagText = (order) => {
  const pickup = isPickupOrder(order);
  const scheduled = isScheduledOrder(order);
  const base = pickup ? "Pickup" : "Delivery";
  return scheduled ? `${base} • Scheduled` : base;
};

// ✅ Styling for tag chip on purple gradient header
const getHeaderTagStyle = (order) => {
  const pickup = isPickupOrder(order);
  const scheduled = isScheduledOrder(order);

  const base = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "8px 16px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 900,
    letterSpacing: 0.25,
    whiteSpace: "nowrap",
    userSelect: "none",
    color: "#ffffff",
    border: "1px solid rgba(255,255,255,0.35)",
    background: "rgba(255,255,255,0.18)",
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    boxShadow: "0 10px 28px rgba(0,0,0,0.14)",
  };

  if (pickup && scheduled) return { ...base, background: "rgba(16,185,129,0.24)" };
  if (pickup) return { ...base, background: "rgba(16,185,129,0.20)" };
  if (scheduled) return { ...base, background: "rgba(168,85,247,0.24)" };
  return { ...base, background: "rgba(59,130,246,0.20)" };
};

// ✅ Brand wordmark (logo-style text)
const ManiDineWordmark = ({ size = 26 }) => {
  return (
    <span
      style={{
        fontFamily: "Poppins, Inter, system-ui, -apple-system, Segoe UI, sans-serif",
        fontWeight: 900,
        fontSize: size,
        letterSpacing: 0.2,
        lineHeight: 1,
        display: "inline-flex",
        alignItems: "baseline",
      }}
    >
      <span style={{ color: "#0f71d4ff" }}>Mani</span>
      <span style={{ color: "#c04309ff" }}>Dine</span>
    </span>
  );
};

const IncomingOrderModal = ({ order, onAccept, onReject, onClose }) => {
  const [readyTimeMinutes, setReadyTimeMinutes] = useState(30);
  const [editMode, setEditMode] = useState(false);
  const [isAccepting, setIsAccepting] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const items = order?.items || [];

  // ✅ NEW: Live listener to auto-close popup if customer cancels (or order is no longer "new")
  useEffect(() => {
    const orderId = order?.id;
    if (!orderId) return undefined;

    const ref = doc(db, "orders", orderId);

    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) return;

        const live = snap.data() || {};
        const status = String(live.status || "").toLowerCase();

        // Debug line (you should now SEE this in browser console when modal is actually used)
        console.log("[POPUP LISTEN]", orderId, status);

        // These are the only "new order" statuses that should keep popup open
        const keepOpen = status === "order_placed" || status === "pending" || status === "new";

        // If customer cancels OR restaurant already accepted OR anything else → close the popup immediately
        if (!keepOpen) {
          if (onClose) onClose({ forceResolve: true, liveStatus: status });
          else if (onReject) onReject();
        }
      },
      (err) => {
        console.error("[IncomingOrderModal] order doc listener error", err);
      }
    );

    return () => unsub();
  }, [order?.id, onClose, onReject]);

  const totals = useMemo(() => calculateTotals(items, order), [items, order]);

  const itemCount = useMemo(
    () => items.reduce((sum, item) => sum + (item.quantity || 1), 0),
    [items]
  );

  if (!order) return null;

  const customerName =
    order.customerName ||
    order.customer?.name ||
    order.customerEmail ||
    order.userId ||
    "Customer";

  const deriveNumericCode = () => {
    const raw = order.orderNumber || order.id || "";
    const digitsOnly = raw.replace(/\D/g, "");
    if (digitsOnly) {
      const trimmed = digitsOnly.slice(-5);
      return trimmed.padStart(4, "0");
    }
    if (!raw) return "";
    let hash = 0;
    for (let i = 0; i < raw.length; i += 1) {
      hash = (hash * 31 + raw.charCodeAt(i)) % 100000;
    }
    return String(hash).padStart(4, "0");
  };

  const orderNumberRaw = deriveNumericCode();
  const orderNumber = orderNumberRaw ? `#${orderNumberRaw}` : "New order";

  const placedAt = order.createdAt || order.placedAt;

  // ✅ Cutlery flag (keep fallbacks for older orders)
  const includeCutlery =
    order.cutleryRequested ??
    order.includeCutlery ??
    order.wantsCutlery ??
    order.cutlery ??
    order.includeUtensils ??
    false;

  const cutleryPreference = order.cutleryPreference;


  const buildAcceptPayload = () => {
    const prepMs = Math.max(0, Number(readyTimeMinutes || 0)) * 60 * 1000;
    const estimatedReadyAt = prepMs > 0 ? new Date(Date.now() + prepMs) : null;

    const payload = {
      readyTimeMinutes: Number(readyTimeMinutes || 0),
      prepTimeMinutes: Number(readyTimeMinutes || 0),
      acceptedAt: new Date(),
      ...(estimatedReadyAt
        ? {
            estimatedReadyAt,
          }
        : {}),
    };

    return payload;
  };

  const handleAccept = async () => {
    const orderId = order.id;

    if (!orderId) {
      if (onAccept) await Promise.resolve(onAccept(buildAcceptPayload()));
      return;
    }

    if (items.length === 0) {
      alert("Order must contain at least one item before accepting.");
      return;
    }

    setIsAccepting(true);

    try {
      if (onAccept) await Promise.resolve(onAccept(buildAcceptPayload()));
    } catch (error) {
      console.error("Failed to accept order", error);
      alert("Could not accept this order. Please try again.");
    } finally {
      setIsAccepting(false);
    }
  };

  const handleReject = async () => {
    if (isAccepting || isRejecting) return;
    if (!order?.id) {
      if (onReject) onReject();
      return;
    }

    try {
      const user = auth.currentUser;
      if (!user) {
        alert("Please sign in again and try rejecting the order.");
        return;
      }

      setIsRejecting(true);

      const token = await user.getIdToken(true);
      const res = await fetch(CANCEL_ORDER_URL, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ orderId: order.id }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || data?.ok === false) {
        const msg =
          data?.error ||
          data?.message ||
          "Could not reject and refund this order. Please try again.";
        console.error("[IncomingOrderModal] Reject/refund failed", data);
        alert(msg);
        return;
      }

      if (onClose) onClose({ forceResolve: true, liveStatus: "cancelled" });
      else if (onReject) onReject();
    } catch (error) {
      console.error("[IncomingOrderModal] Failed to reject/refund order", error);
      alert("Could not reject and refund this order. Please try again.");
    } finally {
      setIsRejecting(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleClose = () => {
    if (isAccepting || isRejecting) return;
    if (onClose) onClose({ userInitiated: true });
    else if (onReject) onReject();
  };

  // ✅ Header tag values
  const headerTagText = getHeaderTagText(order);
  const headerTagStyle = getHeaderTagStyle(order);
  const scheduleLabel = getScheduleLabel(order);

  return (
    <div className="incoming-overlay">
      <div className="incoming-sheet">
        {/* Purple ManiDine-style header */}
        <div
          className="incoming-header"
          style={{
            position: "relative",
            paddingBottom: scheduleLabel ? 86 : 58, // ✅ extra space when scheduled label is shown clearly under the tag
          }}
        >
          <div className="incoming-header-left">
            <p className="incoming-header-subtitle">NEW INCOMING ORDER</p>
            <div className="incoming-header-order-number">{orderNumber}</div>
            <p className="incoming-header-time">{formatDateTime(placedAt) || "Just now"}</p>
          </div>

          {/* Center brand pill */}
          <div className="incoming-brand center">
            <span className="incoming-brand-mark">🍽️</span>
            <span
              className="incoming-brand-text"
              style={{
                display: "inline-flex",
                alignItems: "center",
              }}
            >
              <ManiDineWordmark size={28} />
            </span>
          </div>

          <div className="incoming-header-right">
            <button
              type="button"
              className="incoming-header-close"
              onClick={handleClose}
              aria-label="Close"
              disabled={isAccepting || isRejecting}
            >
              ✕
            </button>
          </div>

          {/* ✅ TAG: ALWAYS visible, bottom-center, not near ManiDine text */}
          <div
            style={{
              position: "absolute",
              left: "50%",
              bottom: 14,
              transform: "translateX(-50%)",
              zIndex: 9999,
              width: "auto",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "auto",
              gap: 8,
            }}
          >
            <span style={headerTagStyle}>{headerTagText}</span>

            {scheduleLabel ? (
              <div
                style={{
                  color: "#ffffff",
                  fontSize: 14,
                  fontWeight: 800,
                  lineHeight: 1.2,
                  textAlign: "center",
                  textShadow: "0 1px 10px rgba(0,0,0,0.16)",
                  whiteSpace: "nowrap",
                }}
              >
                {scheduleLabel}
              </div>
            ) : null}
          </div>
        </div>

        {/* Two-column content */}
        <div className="incoming-content incoming-order-layout">
          {/* LEFT: items */}
          <div className="incoming-main incoming-order-left">
            <div className="incoming-main-title-row">
              <span className="incoming-main-title">{customerName}</span>
              {orderNumberRaw && <span className="incoming-main-order-code"> • {orderNumberRaw}</span>}
            </div>

            <div className="incoming-main-chips">
              <span className="incoming-chip">
                {itemCount} item{itemCount !== 1 ? "s" : ""}
              </span>

              {order.isNewCustomer && <span className="incoming-chip">New customer</span>}
            </div>

            <div className="incoming-main-actions">
              <div className="incoming-cutlery-pill">
                Cutlery:{" "}
                <strong>
                  {cutleryPreference === "disposable_cutlery" ? "Yes" : includeCutlery ? "Yes" : "No"}
                </strong>
              </div>

              <button type="button" className="incoming-print-inline" onClick={handlePrint} disabled={isAccepting || isRejecting}>
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M6 9V4h12v5m-9 7h6m5-3v5H4v-5H3a1 1 0 0 1-1-1v-3a3 3 0 0 1 3-3h14a3 3 0 0 1 3 3v3a1 1 0 0 1-1 1h-1Z"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Print
              </button>
            </div>

            {order.allergyNotes || order.specialInstructions ? (
              <div className="incoming-allergy-strip">
                <span className="emoji">⚠️</span>
                <span>{order.allergyNotes || order.specialInstructions || ""}</span>
              </div>
            ) : null}

            <div className="incoming-items-header">
              <span className="incoming-items-title">Items</span>
            </div>

            <div className="incoming-items-list">
              {items.length === 0 ? (
                <div className="incoming-empty">No items on this order.</div>
              ) : (
                items.map((item, idx) => {
                  const lineTotal = item.price != null ? item.price * (item.quantity || 1) : null;
                  return (
                    <div key={idx} className="incoming-item-row">
                      <div className="incoming-item-left">
                        <div>
                          <div className="incoming-item-name">{item.name || "Item"}</div>
                          {item.modifiers && <div className="incoming-item-modifiers">{item.modifiers}</div>}
                          {item.notes && <div className="incoming-item-notes">“{item.notes}”</div>}
                          <div className="incoming-item-price-per">
                            {item.price != null ? `${formatCurrency(item.price)} each` : "--"}
                          </div>
                        </div>
                      </div>

                      <div className="incoming-item-actions">
                        <div className="incoming-item-qty">{item.quantity || 1} ×</div>
                        <div className="incoming-item-price">
                          {lineTotal != null ? formatCurrency(lineTotal) : "--"}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <div className="incoming-order-items-summary">
              <div className="incoming-order-summary-row">
                <span>Subtotal</span>
                <span>{formatCurrency(totals.subtotal)}</span>
              </div>
              <div className="incoming-order-summary-row">
                <span>Total</span>
                <span>{formatCurrency(totals.total)}</span>
              </div>
            </div>
          </div>

          {/* RIGHT: ready time + actions */}
          <div className="incoming-side incoming-order-right">
            <div className="incoming-order-card incoming-ready-box">
              <div className="incoming-ready-label">Ready time</div>
              <div className="incoming-ready-value incoming-order-ready-time-big">{readyTimeMinutes} min</div>
              <div className="incoming-ready-suggested">Suggested</div>

              <button
                type="button"
                className="incoming-edit-chip"
                onClick={() => setEditMode((prev) => !prev)}
                disabled={isAccepting || isRejecting}
              >
                ✏️ Edit
              </button>

              {editMode && (
                <div className="incoming-ready-edit">
                  <label>
                    Set ready time:
                    <select
                      value={readyTimeMinutes}
                      onChange={(e) => setReadyTimeMinutes(Number(e.target.value))}
                      disabled={isAccepting || isRejecting}
                    >
                      {[10, 15, 20, 25, 30, 35, 40].map((m) => (
                        <option key={m} value={m}>
                          {m} minutes
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              )}
            </div>

            <button
              type="button"
              className="incoming-adjust-button"
              onClick={() => setEditMode(true)}
              disabled={isAccepting || isRejecting}
            >
              Adjust ready time
            </button>

            <button
              type="button"
              className="incoming-accept-button gradient"
              onClick={handleAccept}
              disabled={isAccepting || isRejecting}
            >
              {isAccepting ? "Accepting…" : isRejecting ? "Rejecting…" : "Accept"}
            </button>

            <button
              type="button"
              className="incoming-reject-link"
              onClick={handleReject}
              disabled={isAccepting || isRejecting}
            >
              Cancel / Reject order
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IncomingOrderModal;